<template>
  <div class="card report-options flex flex-column align-item-start">
    <p>
      Сравнивает показатели двух разных групп.
    </p>

    <div class="report-options__body">
      <div>
        <div class="label">
          1-я группа
        </div>
        <a-select
            v-model:value="group1"
            :placeholder="'Выберите группу'"
            style="width: 100%;"
            :not-found-content="'Нет данных'"
            show-arrow
        >
          <a-select-option v-for="item in groupOptions" :key="item.id" :value="item.id">
            {{ item.text }}
          </a-select-option>
        </a-select>
      </div>

      <div>
        <div class="label">
          2-я группа
        </div>
        <a-select
            v-model:value="group2"
            :placeholder="'Выберите группу'"
            style="width: 100%;"
            :not-found-content="'Нет данных'"
            show-arrow
        >
          <a-select-option v-for="item in groupOptions" :key="item.id" :value="item.id">
            {{ item.text }}
          </a-select-option>
        </a-select>
      </div>
    </div>

    <div
        v-if="isReadyForReport"
        class="report-options__btn flex justify-content-center"
    >
      <a-button
          @click="requestForReport"
          class="primary-button"
          :disabled="!canGenerateReport"
      >
        Сформировать отчет
      </a-button>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeMount, onBeforeUnmount, ref } from "@vue/runtime-core";
import CustomSelect2 from "../../../components/Forms/Fields/CustomSelect2.vue";
import { useStore } from "vuex";
import { LeftOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import { useRouter } from "vue-router";
import { getReportDetailsEmployeeCountText } from "./reportDetailsEmployeeCount";

export default {
  components: { LeftOutlined, CustomSelect2 },
  name: "base-report-parametres",

  setup(props, context) {
    const store = useStore();

    const group1 = ref(null);
    const group2 = ref(null);
    const groupOptions = ref([]);
    const lastGeneratedPayload = ref(null);

    const groups = computed(() => store.state.group.groups);
    const user = computed(() => store.state.profile.profile);

    const router = useRouter();

    const isReadyForReport = computed(() => {
      return group1.value !== null && group2.value !== null;
    });

    onBeforeMount(() => {
      store.dispatch("group/getGroups").then(() => {
        groupOptions.value.push({ id: 0, text: "Все сотрудники", users_count: null });

        groupOptions.value = groupOptions.value.concat(groups.value.map((group) => {
          return {
            id: group.id,
            text: group.name,
            users_count: group.users_count,
          };
        }));
      });
    });

    const getTodayDateString = () => {
      const date = new Date();

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${day}.${month}.${year}`;
    };

    const getSelectedGroupObject = (groupId) => {
      if (groupId === 0) {
        return {
          id: 0,
          text: "Все сотрудники",
          users_count: null,
        };
      }

      const foundGroup = groups.value.find((item) => item.id === groupId);

      if (!foundGroup) {
        return {
          id: 0,
          text: "Все сотрудники",
          users_count: null,
        };
      }

      return {
        id: foundGroup.id,
        text: foundGroup.name,
        users_count: foundGroup.users_count,
      };
    };

    const getPayload = () => {
      const firstGroupId = group1.value === 0 ? null : group1.value;
      const secondGroupId = group2.value === 0 ? null : group2.value;

      const payload = {
        first_group_id: firstGroupId,
        second_group_id: secondGroupId,
      };

      if (firstGroupId === null || secondGroupId === null) {
        payload.company_id = user.value?.company?.id ?? null;
      }

      return payload;
    };

    const getSelectedGroupCount = (group) => {
      return group.id === 0 ? "all" : group.users_count;
    };

    const serializePayload = (payload) => {
      return JSON.stringify(payload);
    };

    const isReportAlreadyGenerated = computed(() => {
      if (!lastGeneratedPayload.value) {
        return false;
      }

      return lastGeneratedPayload.value === serializePayload(getPayload());
    });

    const canGenerateReport = computed(() => {
      return isReadyForReport.value && !isReportAlreadyGenerated.value;
    });

    const requestForReport = () => {
      if (!isReadyForReport.value) {
        return;
      }

      if (group1.value === group2.value) {
        message.error("Выберите две разные группы");
        return;
      }

      const payload = getPayload();

      store.dispatch("group/clearAnalyticsGroupData");

      const firstGroup = getSelectedGroupObject(group1.value);
      const secondGroup = getSelectedGroupObject(group2.value);

      store.dispatch("group/setAnalyticsGroupData", {
        group_1: {
          name: firstGroup.text,
          count: firstGroup.users_count ?? null,
        },
        group_2: {
          name: secondGroup.text,
          count: secondGroup.users_count ?? null,
        },
      });

      const reportDetails = {
        groupName: `${firstGroup.text} / ${secondGroup.text}`,
        filterName: "",
        employeeCount: getReportDetailsEmployeeCountText(
            getSelectedGroupCount(firstGroup),
            getSelectedGroupCount(secondGroup)
        ),
        dateString: getTodayDateString(),
        type: "group",
      };

      store.commit("analytics/setCurrentReportDetails", reportDetails);

      lastGeneratedPayload.value = serializePayload(payload);

      context.emit("reportRequest", payload);
    };

    onBeforeUnmount(() => {
      store.commit("analytics/clearCurrentReportDetails");
    });

    return {
      group1,
      group2,
      groupOptions,
      groups,
      isReadyForReport,
      canGenerateReport,
      requestForReport,
      router,
    };
  },
};
</script>

<style scoped lang="scss">
.header {
  position: fixed;
  top: 0;
  padding: 15px 10px 0 10px;
  display: flex;
  justify-content: center;
  row-gap: 10px;
  column-gap: 25px;
  font-size: 20px;
  font-weight: 600;
  z-index: 25;
}

.icon-back {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 24px;
  width: 24px;
  height: 24px;
  background-color: #FFFFFF;
  border-radius: 8px;
  cursor: pointer;

  .icon-arrow {
    width: 14px;
    height: 14px;
  }

  &:hover {
    .icon-arrow {
      color: var(--main-green);
    }
  }
}

.card {
  p {
    padding-left: 0;
    font-size: 14px;
  }
}

.label {
  font-size: 14px;
}

@media (max-width: $mobile-breakpoint) {
  .report-options__btn,
  .primary-button {
    width: 100%;
  }
}

.report-options__body {
  display: flex;
  flex-direction: column;
  row-gap: 15px;
  max-width: 800px;
}
</style>
